import React from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';

const COGNITO_DOMAIN = 'https://percoro-auth.auth.us-east-1.amazoncognito.com';
const CLIENT_ID = '2no7vkuclaq50ff4tgtoapt4l7';

function buildGoogleAuthUrl(): string {
  const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
  return (
    `${COGNITO_DOMAIN}/oauth2/authorize` +
    `?client_id=${CLIENT_ID}` +
    `&response_type=code` +
    `&scope=openid+email+profile` +
    `&redirect_uri=${redirectUri}` +
    `&identity_provider=Google`
  );
}

// Google "G" logo — compliant with Google brand guidelines
function GoogleLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

interface GoogleSignInButtonProps {
  label?: string;
}

// ─── Standalone "Continue with Google" button ─────────────────────────────────
// Drop into any page. Redirects to Cognito OAuth → Google → /auth/callback.
export function GoogleSignInButton({ label = 'Continue with Google' }: GoogleSignInButtonProps) {
  const handleClick = () => {
    window.location.href = buildGoogleAuthUrl();
  };

  return (
    <Button
      fullWidth
      variant="outlined"
      onClick={handleClick}
      startIcon={<GoogleLogo />}
      sx={{
        py: 1.25,
        borderRadius: '30px',
        textTransform: 'none',
        fontSize: '1rem',
        fontWeight: 500,
        color: '#3c4043',
        borderColor: '#dadce0',
        bgcolor: 'white',
        '&:hover': {
          bgcolor: '#f8f9fa',
          borderColor: '#c1c1c1',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        },
      }}
    >
      {label}
    </Button>
  );
}

// ─── "or continue with email" divider ─────────────────────────────────────────
export function GoogleSignInDivider() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: 2.5 }}>
      <Divider sx={{ flex: 1 }} />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mx: 2, whiteSpace: 'nowrap' }}
      >
        or continue with email
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Box>
  );
}
